import {
  type QueryClient,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
} from '@tanstack/react-query';
import { ENDPOINT, PAGINATION_PAGE_SIZE_DEFAULT } from '~/constants';

import { type AssetApiResponse } from '~/types/assets';

import { vestigasApi } from '~/services/kyClient';

import {
  type AssetType,
  type ReparsingCategory,
} from '~/modules/asset-reparsing/types';

import { i18n } from '~/utils';

import { mapReparsingAssetsData } from './mapReparsingAssetsQueryData';
import { queryKeysAssetReparsing } from './queryKeys';

/**
 * Picks data from the list of assets to render the tasks list.
 * The assets are already transformed from the raw API response in the `mapReparsingAssetsData` function.
 */
const mapTasks = (assets: AssetType[] = []) =>
  assets?.map(({ assetId, header, dlnDate, assetParties, reparsingId }) => {
    const supplierName = assetParties?.supplier?.legalOrganization?.name;
    const recipientName = assetParties?.recipient?.legalOrganization?.name;

    return {
      assetId,
      dlnDate,
      dlnNumber: header.id,
      headerId: header,
      recipientName,
      reparsingId,
      searchString: [assetId, header.id, supplierName, recipientName]
        .filter(Boolean)
        .map((item) => String(item).toLowerCase())
        .join(';'),
      supplierName,
    };
  });

/**
 * Gets all assets saved for potential reparsing.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/get_assets_for_reparsing_vadmin_asset_reparsing_all_get
 */
const getAllReparsingAssets = async (
  searchParams?: Record<string, string | number>,
) => {
  try {
    const response = await vestigasApi
      .get(ENDPOINT.ASSET_REPARSING.GET_ALL(), { searchParams })
      .json<AssetApiResponse>();
    return response;
  } catch (error) {
    // Attach custom message to error object.
    const customError = new Error(
      i18n.t(($) => $.assetReparsing.query.reparsingAssets.error.generic),
    );

    console.error('Error fetching assets for reparsing:', error);

    // Re-throw the error to propagate it to the calling function.
    throw customError;
  }
};

type ReparsingAssetsQueryOptions = {
  initialData?: any[];
  limit: number;
  offset: number;
  reparsingCategory?: ReparsingCategory;
  // reparsingTaskTypes?: ReparsingTaskType[];
};

const defaultQueryOptions: ReparsingAssetsQueryOptions = {
  limit: PAGINATION_PAGE_SIZE_DEFAULT,
  offset: 0,
  reparsingCategory: 'frontend',
};

/**
 * Function to generate the react-query config for fetching reparsing assets.
 * Reusable within the useQuery hook and for the react-router loader.
 *
 * @return {Object} The query object containing query key, query function, refetch interval, assets, and tasks.
 */
const reparsingAssetsQuery = (
  options: ReparsingAssetsQueryOptions = defaultQueryOptions,
): UseInfiniteQueryOptions<any, Error, any, any> => {
  const { limit, reparsingCategory } = options;

  return {
    getNextPageParam(lastPage: AssetApiResponse, pages: AssetApiResponse[]) {
      const totalRecords = lastPage.totalCount;
      const nextOffset = pages.length * limit;

      return nextOffset < totalRecords ? nextOffset : undefined;
    },
    initialPageParam: 0,
    async queryFn({ pageParam: pageParameter = 0 }) {
      return getAllReparsingAssets({
        limit: 300, // temporarily bump the limit to 300 to avoid pagination issues
        offset: pageParameter as number,
        reparsingCategory,
      });
    },
    queryKey: queryKeysAssetReparsing.getAllInfinite({ reparsingCategory }),
    select(data) {
      const assets = data.pages.flatMap(({ data }) =>
        mapReparsingAssetsData(data),
      );

      const tasks = mapTasks(assets);

      return {
        assets,
        tasks,
      };
    },
  };
};

/**
 * A custom hook to fetch all reparsing assets.
 * Uses the `useQuery` hook from `@tanstack/react-query` internally.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/get_assets_for_reparsing_vadmin_asset_reparsing_all_get
 *
 * @return {UseQueryResult<any[]>} The result of the query, which is an array of reparsing assets.
 */
export const useQueryReparsingAssets = (options: ReparsingAssetsQueryOptions) =>
  useInfiniteQuery(reparsingAssetsQuery(options));

/**
 * TanStack Router loader function for reparsingAssetsQuery.
 * Allows already fetching the data when the route is visited.
 * The useQuery hook of useQueryReparsingAssets can only run when the component is mounted, which happens much later.
 *
 * @param {QueryClient} queryClient - The query client for fetching data.
 * @return {Promise<any>} The data fetched or returned from the query.
 */
export const assetReparsingLoader =
  (
    queryClient: QueryClient,
    options: ReparsingAssetsQueryOptions = defaultQueryOptions,
  ) =>
  async () => {
    const query = reparsingAssetsQuery(options);

    // Return data from cache or fetch it.
    return (
      queryClient.getQueryData(query.queryKey) ??
      (await queryClient.fetchQuery(query))
    );
  };
